import React, { createRef } from 'react';
import * as Inovx from '@shared';
import Viewer from './Viewer3d/Viewer3D';
import ReactViewer3DConstants from './ReactViewer3DConstants';
import ReactViewer3DInternalConstants from './ReactViewer3DInternalConstants';
import Viewer3DManager from '../Managers/Viewer3DManager';
import * as Interfaces from '../../Interfaces/Interfaces';
import ViewerSettings from '../../constants/Viewer3DConstants';
import { Quaternion } from 'babylonjs/Maths/math.vector';
import ViewerStore from '../../stores/ViewerStore';
import { AssetGeometryPendingContext } from '../../Interfaces/ViewerInterfaces';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

interface ReactViewer3DProps {
    viewerId: string;
    lodData: string;
    assetObjectListId: string;
    projectId: string;
    ontologyBoundsContext?: IOntologyBoundsContext;
    namedLists: Interfaces.Interfaces.OverrideNamedList[];
    viewerConfig: IFacilityConfig;
    camPosition?: IVector3D;
    camFocalDistance? : number,
    camYaw?: number;
    camPitch?: number;
    rangePosition: IVector3D;
    distanceThreshold?: number;
    assetSets?: IAssetSet[];
    assetSetIsolation?: boolean;
    assetGeometryContextPending?: boolean;
    awaitFirstAssetCoordinatorProgress?: boolean;
    assetGeometryPendingContext?: AssetGeometryPendingContext;
    onViewerSelectionChanged?: (objectIDs: string[], allObjectIDs: any[], isSelected: boolean, type: "object" | "scan" | "mesh" | "smartmesh" | "pagedmesh" | "annotation" | "assembly", ev?: PointerEvent) => void;
    onViewerSelectionModeChanged?: (enabled:boolean) => void;
    onViewerPoint3DClicked?: (point3D: IVector3D, screenPoint: IVector2D, cameraPosition: IVector3D) => void;
    onVolumeTagChanged?: (tag: IAnnotationDisplayItem) => void;
    onViewerInitialized?: () => void;
    onCameraChanged?: (position: IVector3D, target: IVector3D, rotatedDegrees: number) => void;
    onScanPositionHighlighted?: (view: IView360Detail | null) => void;
    onScanPositionRightClicked?: (view: IView360Detail | string | null, position?: { clientX: number; clientY: number } | null) => void;
    onAssemblyCreated?: (name: string, assemblyTemplate: string, position: IVector3D, orientation: IVector3D, instanceScale: IVector3D) => void;
    onAssemblyUpdated?: (id: string, position: IVector3D, orientation: IVector3D, instanceScale: IVector3D) => void;
    onSettingsChanged?: (name: string, value: string) => void;
}

var _reactViewer: ReactViewer3D = null;
var _queuedActions: any = {};

function getViewerSettings(config: IFacilityConfig): ISetting[] {
    const viewerSettings: ISetting[] = [
        { Name: ViewerSettings.Viewer3DSettingsEnums.CanvasMargin, Value: "Fill" },
        { Name: ViewerSettings.Viewer3DSettingsEnums.Compass, Value: config.PanoCompassVisible ? "Show" : "Hide" },
        { Name: ViewerSettings.Viewer3DSettingsEnums.ShowCrosshair, Value: config.ShowCrosshair ? "Show" : "Hide" },
        { Name: ViewerSettings.Viewer3DSettingsEnums.Slider, Value: config.ZoomSliderVisible ? "Show" : "Hide" },
        { Name: ViewerSettings.Viewer3DSettingsEnums.Shader, Value: config.EdgeShader ? "Show" : "Hide" },
        { Name: ViewerSettings.Viewer3DSettingsEnums.InvertY, Value: config.InvertY ? "Show" : "Hide" },
        { Name: ViewerSettings.Viewer3DSettingsEnums.BackgroundColor, Value: config.BackgroundColor },
        { Name: ViewerSettings.Viewer3DSettingsEnums.MeasurementColor, Value: config.MeasurementColor },
        { Name: ViewerSettings.Viewer3DSettingsEnums.TagVolumeBoxColor, Value: config.TagVolumeBoxColor },
        { Name: ViewerSettings.Viewer3DSettingsEnums.PanoScanSphereColor, Value: config.PanoScanSphereColor },
        { Name: ViewerSettings.Viewer3DSettingsEnums.PanoImageSphereColor, Value: config.PanoImageSphereColor },
        { Name: ViewerSettings.Viewer3DSettingsEnums.MemoryProfile, Value: config.MemoryProfile },
        { Name: ViewerSettings.Viewer3DSettingsEnums.ViewerMemoryBudget, Value: config.ViewerMemoryBudget },
        { Name: ViewerSettings.Viewer3DSettingsEnums.MeshResolution, Value: config.MeshResolution },
        { Name: ViewerSettings.Viewer3DSettingsEnums.CameraType, Value: config.CameraType },
        { Name: ViewerSettings.Viewer3DSettingsEnums.RotationSensitivity, Value: config.RotationSensitivity },
        { Name: ViewerSettings.Viewer3DSettingsEnums.VelocitySensitivity, Value: config.VelocitySensitivity }

    ];  
    return viewerSettings;
}

class ReactViewer3D extends React.Component<ReactViewer3DProps, any> {
    _viewerRef = createRef<HTMLDivElement>();
    _viewer: Viewer;
    _viewerManager: Viewer3DManager;
    _viewerId: string;

    constructor(props: ReactViewer3DProps) {
        super(props);

    }

    private _handleActions = () => {
        _reactViewer = this;
    }

    private _initalizeViewer = () => {

        var footer = document.getElementById("footer-panel") as HTMLDivElement;
        this._viewerManager = new Viewer3DManager(UrlUtilities.getApiUrl(), this._viewerRef.current as any, footer, false);

        const options = {
            Region_Megabyte_Memory_Limit: 500,
            LOD_Point_Sizes: [2, 2, 3, 3],
            LOD_Distance_Thresholds: [10, 20, 50, 100, 500, 1000],
            Minimum_Meshes_Displayed: 2
        };

        const optionsString = JSON.stringify(options);
        this._viewer = this._viewerManager.createViewer(optionsString, this.props.lodData, false, true);

        if (this.props.onViewerSelectionChanged) {
            this._viewer.registerEvent("SELECTION_CHANGED", (e: Interfaces.Interfaces.ISelectionChangedEvent) => {
                this.props.onViewerSelectionChanged(e.objectIds, e.allSelectedObjects, e.isSelected,
                    (e.objectSelectionType == "scan") ? "scan"
                        : (e.objectSelectionType == "pagedmesh") ? "pagedmesh"
                            : (e.objectSelectionType == "mesh") ? "smartmesh"
                                : (e.objectSelectionType == "glbobject") ? "mesh"
                                    : (e.objectSelectionType == "annotation") ? "annotation"
                                        : (e.objectSelectionType == "assembly") ? "assembly"
                                            : "object", e.ev);
            });
        }
        if (this.props.onViewerSelectionModeChanged) {
            this._viewer.registerEvent("SELECTION_MODE_CHANGED", (e: Interfaces.Interfaces.ISelectionModeChangedEvent) => {
                this.props.onViewerSelectionModeChanged(e.enabled);
            });
        }
        if (this.props.onVolumeTagChanged) {
            this._viewer.registerEvent("VOLUME_TAG_CHANGED", (e: any) => {
                this.props.onVolumeTagChanged(e);
            });
        }
        if (this.props.onViewerPoint3DClicked) {
            this._viewer.registerEvent("POINT3D_CLICKED", (e: Interfaces.Interfaces.IPoint3DClickedEvent) => {
                this.props.onViewerPoint3DClicked(e.point3D, e.point2D, e.cameraPosition);
            });
        }
        if (this.props.onCameraChanged) {
            this._viewer.registerEvent("CAMERA_CHANGED", (e: Interfaces.Interfaces.ICameraChangedEvent) => {
                this.props.onCameraChanged({ X: e.pos.x, Y: e.pos.y, Z: e.pos.z }, { X: e.target.x, Y: e.target.y, Z: e.target.z }, e.rotatedDegrees);
            });
        }
        if (this.props.onScanPositionHighlighted) {
            this._viewer.registerEvent("SCAN_POSITION_HIGHLIGHTED", (e: any) => {
                // supports either { view } or CustomEvent<{ view }>
                const view: IView360Detail | null = e?.view ?? e?.detail?.view ?? null;
                this.props.onScanPositionHighlighted(view);
            });
        }
        if (this.props.onScanPositionRightClicked) {
            this._viewer.registerEvent("SCAN_POSITION_RIGHT_CLICKED", (e: any) => {
                const view: IView360Detail | string | null = e?.view ?? e?.detail?.view ?? null;
                const position = e?.position ?? e?.detail?.position ?? null;
                this.props.onScanPositionRightClicked(view, position);
            });
        }
        if (this.props.onAssemblyCreated) {
            this._viewer.registerEvent("ASSEMBLY_CREATED", (e: Interfaces.Interfaces.IAssemblyCreatedEvent) => {
                this.props.onAssemblyCreated(e.Name, e.AssemblyTemplateID, e.Position, e.Orientation, e.InstanceScale);
            });
        }
        if (this.props.onAssemblyUpdated) {
            this._viewer.registerEvent("ASSEMBLY_UPDATED", (e: Interfaces.Interfaces.IAssemblyUpdatedEvent) => {
                this.props.onAssemblyUpdated(e.ID, e.Position, e.Orientation, e.InstanceScale);
            });
        }
        if (this.props.onSettingsChanged) {
            this._viewer.registerEvent("VIEWER_SETTING_CHANGED", (e: Interfaces.Interfaces.ISettingUpdateEvent) => {
                this.props.onSettingsChanged(e.Name, e.Value);
            });
        }

        var settings: Interfaces.Interfaces.IDisplaySettings = {
            ViewerFillStyle: 1,
            DisplayStyle: 1,
            BackgroundSceneType: 1,
            BackgroundColor: "#000000",
            CanvasWidth: 1000,
            CanvasHeight: 500,
            MeshColorEnabled: false
        };

        this._viewerManager.setDefaultView(settings);
        this._viewerManager.repositionToolbar();

        if (this.props.viewerConfig) {
            this._viewerManager.receiveSettings(getViewerSettings(this.props.viewerConfig));
        }
    }

    componentDidMount() {
        var that = this;
        this._viewerId = this.props.viewerId;

        this._initalizeViewer();

        this._handleActions();
        const assetGeometryContextPending = !!this.props.assetGeometryContextPending;
        const awaitFirstAssetCoordinatorProgress =
            !assetGeometryContextPending && !!this.props.awaitFirstAssetCoordinatorProgress;
        this._viewer.setAssetGeometryContextPending(
            assetGeometryContextPending,
            awaitFirstAssetCoordinatorProgress
        );
        this._viewerManager.setAssetGeometryContextPending(
            assetGeometryContextPending,
            awaitFirstAssetCoordinatorProgress,
            this.props.assetGeometryPendingContext
        );
        var that = this;

        this._viewer.requestData(

            this.props.assetObjectListId, this.props.projectId, this.props.namedLists, this.props.ontologyBoundsContext, this.props.camPosition, this.props.camPitch, this.props.camYaw, this.props.rangePosition, this.props.distanceThreshold, this.props.camFocalDistance, this.props.assetSets, this.props.assetSetIsolation,
            function () {
                that.props.onViewerInitialized?.();
                (that._viewerManager as any)._createSettingsMenu();
            });

        if (Object.getOwnPropertyNames(_queuedActions).length > 0) {
            for (var viewerId in _queuedActions) {
                if (this._viewerId === viewerId) {
                    _queuedActions[viewerId]();
                }
            }
        }


    }

    shouldComponentUpdate(nextProps: ReactViewer3DProps, nextState: any) {
        // Never update this component...
        return false;
    }

    componentWillUnmount() {
        if (_reactViewer === this) {
            _reactViewer = null;
        }

        this._viewerManager.dispose();
    }

    render() {
        var styles = {
            display: 'inline-block',
            position: 'relative' as const,
            width: '100%'
        };
        return (<div style={styles} ref={this._viewerRef}></div>);
    }
}

Inovx.Shared.Flux.AppDispatcher.get(ReactViewer3DInternalConstants.DISPATCHER_NAME).register(function (payload: any) {
    
    switch (payload.actionType) {
        case ReactViewer3DConstants.RV_HIGHLIGHT_SCAN_POSITION:
            if (_reactViewer && payload.viewerId === _reactViewer._viewerId) {
                (_reactViewer._viewer as any)?.highlightScanPosition?.(payload.viewId);
            }
            break;
        case ReactViewer3DConstants.RV_SET_ANNOTATIONS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setAnnotations(payload.annotations);
            }
            break;
        case ReactViewer3DConstants.RV_SET_SCAN_POSITIONS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setScanPositions(payload.scanPositions);
            }
            break;
        case ReactViewer3DConstants.RV_SET_SELECTED_ASSET_SET:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setSelectedAssetSet(payload.assetSet);
            }
            break;
        case ReactViewer3DConstants.RV_SET_RANGE_POSITION:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                Promise.resolve(_reactViewer._viewer.setupViewerData(
                    payload.ontologyId,
                    payload.projectId,
                    payload.ontologyBoundsContext,
                    payload.rangePosition,
                    payload.rangeDistance,
                    payload.assetSets,
                    payload.cameraPosition,
                    payload.cameraDirection,
                    payload.focalDistance,
                    payload.repositionCamera
                )).then(() => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_VIEWER_DATA_SETUP,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId
                        });
                }).catch(error => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_VIEWER_DATA_SETUP,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId,
                            error
                        });
                });
            }
            break;
        case ReactViewer3DConstants.RV_REFRESH_ASSET_SET_OCTREES:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                Promise.resolve(_reactViewer._viewer.refreshAssetSetOctrees(
                    payload.assetSets,
                    !!payload.assetSetIsolation
                )).then(() => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_VIEWER_DATA_SETUP,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId
                        });
                }).catch(error => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_VIEWER_DATA_SETUP,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId,
                            error
                        });
                });
            }
            break;
        case ReactViewer3DConstants.RV_INVALIDATE_ASSET_SET_OCTREE_UPDATES:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.invalidateAssetSetOctreeUpdates();
            }
            break;
        case ReactViewer3DConstants.RV_SET_MEASUREMENTS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setMeasurements(payload.measurements);
            }
            break;
        case ReactViewer3DConstants.RV_SET_ASSEMBLY_TEMPLATES:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                const promiseResult = _reactViewer._viewer.setAssemblyTemplates(payload.assemblyTemplates);
                if (payload.callback) {
                    promiseResult.then(payload.callback);
                }
            }
            break;
        case ReactViewer3DConstants.RV_SET_TAG_EDIT_MODE:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setTagEditMode(payload.mode);
            }
            break;
        case ReactViewer3DConstants.RV_SET_ASSEMBLY_EDIT_MODE:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setAssemblyEditMode(payload.mode);
            }
            break;
        case ReactViewer3DConstants.RV_SET_ASSEMBLIES:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setAssemblies(payload.assemblies);
            }
            break;
        case ReactViewer3DConstants.RV_SET_ASSEMBLY_CREATION_TEMPLATE:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setAssemblyCreationTemplate(payload.assemblyCreationTemplate);
            }
            break;
        case ReactViewer3DConstants.RV_CLEAR_MEASUREMENTS:
            if (_reactViewer !== null && payload.viewerId == _reactViewer._viewerId) {
                _reactViewer._viewer.clearMeasurements();
            }
            break;
        case ReactViewer3DConstants.RV_SET_MEASUREMENT_TYPE:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setMeasurementType(payload.measurementType);
            }
            break;
        case ReactViewer3DConstants.RV_FOCUS_CAMERA:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.focusCamera(payload.focusPosition);
            }
            break;
        case ReactViewer3DConstants.RV_ZOOM_CAMERA:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.zoomToExtent(payload.minZoom, payload.maxZoom);
            }
            break;
        case ReactViewer3DConstants.RV_SET_CAMERA:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                const cameraPromise = _reactViewer._viewer.setCamera(
                    payload.cameraPosition,
                    payload.focusPosition,
                    payload.skipAnimation
                );
                if (payload.onComplete) {
                    void Promise.resolve(cameraPromise).then(payload.onComplete, payload.onComplete);
                }
            }
            break;
        case ReactViewer3DConstants.RV_SET_ASSET_GEOMETRY_CONTEXT_PENDING:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewerManager.setAssetGeometryContextPending(
                    !!payload.pending,
                    !!payload.awaitFirstAssetCoordinatorProgress,
                    payload.pendingContext
                );
                _reactViewer._viewer.setAssetGeometryContextPending(
                    !!payload.pending,
                    !!payload.awaitFirstAssetCoordinatorProgress
                );
            }
            break;
        case ReactViewer3DConstants.RV_SET_HOME_CAMERA:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewerManager.setHomeCamera(
                    payload.cameraPosition,
                    payload.focusPosition
                );
            }
            break;
        case ReactViewer3DConstants.RV_HIGHLIGHT_ASSETS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.highlightObjects(payload.ids, true);
            }
            break;
        case ReactViewer3DConstants.RV_UNHIGHLIGHT_ASSETS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.unhighlightAssets(payload.ids, payload.objectType);
            }
            break;
        case ReactViewer3DConstants.RV_UNHIGHLIGHT_ALL_ASSETS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.unhighlightAllObjects();
            }
            break;
        case ReactViewer3DConstants.RV_SET_VIEWER_CLICK_MODE:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setViewerClickMode(payload.mode === "point3D" ? "point3D" : "select");
            }
            break;
        case ReactViewer3DConstants.RV_POPULATE_ASSET_SETS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.populateAssetSets(payload.assetSetData);

                setTimeout(() => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_ASSET_SETS_POPULATED,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId
                        });
                }, 0);
            }
            break;
        case ReactViewer3DConstants.RV_APPLY_ASSET_SET_MEMBERSHIP_DELTA:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.applyAssetSetMembershipDelta(
                    payload.activatedSets || [],
                    payload.deactivatedSetIds || []
                );

                setTimeout(() => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_ASSET_SETS_POPULATED,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId
                        });
                }, 0);
            }
            break;
        case ReactViewer3DConstants.RV_DISPLAY_KV_ASSET_SETS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.displayKVAssetSets(payload.assetSetData);
            }
            break;
        case ReactViewer3DConstants.RV_COLORIZE_ASSET_SETS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {

                _reactViewer._viewer.colorizeAssetSets(
                    payload.assetSetData,
                    !!payload.strictIsolation
                );
                
                setTimeout(() => {

                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_ASSET_SETS_COLORIZED,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId
                        });
                }, 0);
            }
            break;
        case ReactViewer3DConstants.RV_SET_ADVANCED_MATERIAL_TUNINGS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setAdvancedMaterialTunings(payload.tunings);
            }
            break;
        case ReactViewer3DConstants.RV_DESTROY_ASSET_SETS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.destroyAllAssetSets();
            }
            break;
        case ReactViewer3DConstants.RV_SET_VIEWER_CONFIGURATION:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewerManager.receiveSettings(getViewerSettings(payload.configuration));
            }
            break;
        case ReactViewer3DConstants.RV_SET_CONTENT_LAYERS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewerManager?.setContentLayers(payload.layers);
            }
            break; 
        case ReactViewer3DConstants.RV_SET_VIEWER_SETTINGS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewerManager?.setViewerSettings(payload.settings);
            }
            break;
        case ReactViewer3DConstants.RV_SET_SHOW_OCCLUDED_TAGS:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewer.setShowOccludedTags(payload.show);
            }
            break;
        case ReactViewer3DConstants.RV_GET_SCREENSHOT:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewerManager?.get3DScreenshot((image: string) => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_SCREENSHOT_READY,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId,
                            image
                        });
                }, payload.width, payload.height, payload.includeOverlays !== false);
            }
            break;
        case ReactViewer3DConstants.RV_GET_PANORAMA:
            if (_reactViewer !== null && payload.viewerId === _reactViewer._viewerId) {
                _reactViewer._viewerManager?.get3DPanoramaImage((image: string) => {
                    Inovx.Shared.Flux.AppDispatcher
                        .get(ReactViewer3DInternalConstants.DISPATCHER_NAME)
                        .dispatch({
                            actionType: ReactViewer3DConstants.RV_PANORAMA_READY,
                            viewerId: payload.viewerId,
                            requestId: payload.requestId,
                            image
                        });
                }, payload.size);
            }
            break;
    }
});

export default ReactViewer3D;
